import { useCallback, useMemo, useRef, useState } from 'react'
import { AnyComponentRef } from './AnyComponent.types'
import {
    CalculatedId,
    InputArrayNode,
    InputInnerNode,
    InputNode,
    ValidatedForm,
} from './AnyInputHook.types'
import { FIX_EXTERNAL_ID_REGEX, ExternalIdValidator } from '../validators'

const useValidatedForm = <R>(
    requiredRoot: boolean = true
): ValidatedForm<R> => {
    const validations = useRef<InputNode>({})
    const arrayValidations = useRef<InputArrayNode>({})
    const innerValidations = useRef<InputInnerNode>({})
    const dirtyRef = useRef<boolean>()

    const validate = useCallback(
        (validations: InputNode, required: boolean = true) => {
            const names = Object.keys(validations)
            let valid = names.length > 0 || !required

            for (let name of names) {
                const ref = validations[name]
                if (!ref.valid) {
                    ref.setBlur()
                    valid = false
                }
            }
            return valid
        },
        []
    )

    const isValid = useCallback(() => {
        const validRoot = validate(validations.current, requiredRoot)
        const validArray = Object.values(arrayValidations.current)
            .flatMap((el) => el)
            .map((el) => validate(el))
            .reduce((a, b) => a && b, true)
        const validInner = Object.values(innerValidations.current)
            .map((el) => validate(el))
            .reduce((a, b) => a && b, true)
        return validRoot && validArray && validInner
    }, [requiredRoot, validate])

    const checkDirty = useCallback((validations: InputNode) => {
        const names = Object.keys(validations)
        for (let name of names) {
            const ref = validations[name]
            if (ref.dirty) {
                return true
            }
        }
        return false
    }, [])

    const isDirty = useCallback(() => {
        if (dirtyRef.current === undefined) {
            const dirtyRoot = checkDirty(validations.current)

            const dirtyArray = Object.values(arrayValidations.current)
                .flatMap((el) => el)
                .map((el) => checkDirty(el))
                .reduce((a, b) => a || b, false)

            const dirtyInner = Object.values(innerValidations.current)
                .map((el) => checkDirty(el))
                .reduce((a, b) => a || b, false)

            return dirtyRoot || dirtyArray || dirtyInner
        }

        return dirtyRef.current
    }, [dirtyRef, checkDirty])

    const setPristine = useCallback((dirty = false) => {
        dirtyRef.current = dirty
    }, [])

    const register = useCallback((name: string, ref: AnyComponentRef<any>) => {
        validations.current[name] = ref
    }, [])

    const registerArray = useCallback(
        (
            property: string,
            index: number,
            name: string,
            ref: AnyComponentRef<any>
        ) => {
            if (!arrayValidations.current[property]) {
                arrayValidations.current[property] = []
            }

            if (!arrayValidations.current[property][index]) {
                arrayValidations.current[property][index] = {}
            }

            const updated = arrayValidations.current[property][index]
            updated[name] = ref

            arrayValidations.current[property][index] = updated
        },
        []
    )

    const registerInner = useCallback(
        (property: string, name: string, ref: AnyComponentRef<any>) => {
            if (!innerValidations.current[property]) {
                innerValidations.current[property] = {}
            }

            const updated = innerValidations.current[property]
            updated[name] = ref

            innerValidations.current[property] = updated
        },
        []
    )

    const clearArray = useCallback(() => {
        arrayValidations.current = {}
    }, [])

    const clearInner = useCallback(() => {
        innerValidations.current = {}
    }, [])

    const bindInputNode = useCallback((el: InputNode): any => {
        if (!el) return {}

        let base = {} as any
        for (let name of Object.keys(el)) {
            const ref = el[name]
            base = { ...base, [name]: ref.value }
        }

        return base
    }, [])

    const bindArray = useCallback((): any => {
        let base = {} as any
        for (let arrayName of Object.keys(arrayValidations.current)) {
            const inputNodes = arrayValidations.current[arrayName] ?? []
            const array = inputNodes
                .filter((el) => !!el)
                .map((el) => bindInputNode(el))

            base = { ...base, [arrayName]: array }
        }

        return base
    }, [bindInputNode])

    const bindInner = useCallback((): any => {
        let base = {} as any
        for (let name of Object.keys(innerValidations.current)) {
            const el = innerValidations.current[name]
            if (el) {
                base = { ...base, [name]: bindInputNode(el) }
            }
        }

        return base
    }, [bindInputNode])

    const bind = useCallback((): R => {
        const root = bindInputNode(validations.current)
        // proccessing array nodes
        const array = bindArray()
        // proccessing inner nodes
        const inner = bindInner()

        return { ...root, ...array, ...inner } as R
    }, [bindInputNode, bindArray, bindInner])

    return {
        isValid,
        isDirty,
        register,
        bind,
        registerArray,
        registerInner,
        clearArray,
        clearInner,
        setPristine,
    }
}

const calculateId = (name: string, currentId?: string) => {
    if (currentId) {
        return
    }

    return name
        .trim()
        .toLowerCase()
        .normalize('NFKD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(FIX_EXTERNAL_ID_REGEX, '_')
}

const useCalculatedId = (id?: string): CalculatedId => {
    const [initialId, setInitialId] = useState<string | undefined>(id)
    const validators = useMemo(() => [new ExternalIdValidator()], [])

    const hasChangedName = useCallback(
        (name: string, value?: unknown) => {
            if (value === undefined || typeof value !== 'string') return

            const calculatedId = calculateId(value as string, id)
            if (calculatedId !== undefined) {
                setInitialId(calculatedId)
            }
        },
        [id]
    )

    return {
        initialId,
        hasChangedName,
        validators,
    }
}

export { useValidatedForm, useCalculatedId }
